import React, { useState } from "react";
import { X } from "lucide-react";
import MembersTab from "./MembersTab";
import GroupsTab from "./GroupsTab";
import TripsTab from "./TripsTab";
import ExternalContactsTab from "./ExternalContactsTab";
import VenuesTab from "./VenuesTab";

const SettingsModal = ({
  onClose,
  members,
  groups,
  onSaveMembers,
  onSaveGroups,
  onReload,
  session,
  preferences,
  onSavePreferences,
  adminPrefs,
  onSaveAdminPrefs,
  vacationBlocks,
  onReloadVacations,
  onRequestNotifications,
}) => {
  const [activeTab, setActiveTab] = useState("members");
  const [localPrefs, setLocalPrefs] = useState({
    weekStartsMonday: preferences?.weekStartsMonday ?? false,
    defaultView: preferences?.defaultView ?? "month",
  });
  const [prefSaved, setPrefSaved] = useState(false);
  const [localAdmin, setLocalAdmin] = useState({
    isAdmin: adminPrefs?.isAdmin ?? false,
    reminderPhone: adminPrefs?.reminderPhone ?? "",
    anniversaryDate: adminPrefs?.anniversaryDate ?? "",
  });
  const [adminSaved, setAdminSaved] = useState(false);

  const handleSavePrefs = async () => {
    await onSavePreferences(localPrefs);
    setPrefSaved(true);
    setTimeout(() => setPrefSaved(false), 2000);
  };

  const handleSaveAdmin = async () => {
    await onSaveAdminPrefs(localAdmin);
    setAdminSaved(true);
    setTimeout(() => setAdminSaved(false), 2000);
  };

  const tabs = [
    { id: "members", label: "Members" },
    { id: "groups", label: "Groups" },
    { id: "settings", label: "Settings" },
    { id: "vacations", label: "Trips" },
    { id: "contacts", label: "Contacts" },
    { id: "venues", label: "Venues" },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-4 sm:p-6 max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800">Manage Family</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-2 touch-manipulation">
            <X size={24} />
          </button>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 sm:gap-2 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium touch-manipulation ${
                activeTab === tab.id ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "members" && (
          <MembersTab members={members} onReload={onReload} session={session} />
        )}

        {activeTab === "groups" && (
          <GroupsTab groups={groups} members={members} onReload={onReload} />
        )}

        {activeTab === "settings" && (
          <div className="space-y-5">
            {/* Notifications */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">Push Notifications</h4>
              {typeof Notification === "undefined" ? (
                <p className="text-sm text-gray-500">Not supported in this browser</p>
              ) : Notification.permission === "granted" ? (
                <div className="flex items-center gap-2 text-green-700 text-sm">
                  <span>✓</span>
                  <span>Notifications are enabled</span>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    Enable to receive reminders for events.
                  </p>
                  <button
                    onClick={onRequestNotifications}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium touch-manipulation"
                  >
                    Enable Notifications
                  </button>
                </div>
              )}
            </div>

            {/* First day of week */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3">Week starts on</h4>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setLocalPrefs((p) => ({ ...p, weekStartsMonday: false }))}
                  className={`py-2 rounded-lg text-sm font-medium touch-manipulation ${
                    !localPrefs.weekStartsMonday ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-700"
                  }`}
                >
                  Sunday
                </button>
                <button
                  onClick={() => setLocalPrefs((p) => ({ ...p, weekStartsMonday: true }))}
                  className={`py-2 rounded-lg text-sm font-medium touch-manipulation ${
                    localPrefs.weekStartsMonday ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-700"
                  }`}
                >
                  Monday
                </button>
              </div>
            </div>

            {/* Default view */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3">Default view on open</h4>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {["day", "week", "weekend", "month"].map((v) => (
                  <button
                    key={v}
                    onClick={() => setLocalPrefs((p) => ({ ...p, defaultView: v }))}
                    className={`py-2 rounded-lg text-sm font-medium capitalize touch-manipulation ${
                      localPrefs.defaultView === v ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleSavePrefs}
              className={`w-full py-3 rounded-lg font-medium touch-manipulation text-sm ${
                prefSaved ? "bg-green-600 text-white" : "bg-purple-600 text-white hover:bg-purple-700"
              }`}
            >
              {prefSaved ? "✓ Saved!" : "Save Settings"}
            </button>

            {/* Admin reminders */}
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-800">Birthday & Anniversary Reminders</h4>
                <button
                  onClick={() => setLocalAdmin(p => ({ ...p, isAdmin: !p.isAdmin }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${localAdmin.isAdmin ? "bg-purple-600" : "bg-gray-300"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${localAdmin.isAdmin ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
              {localAdmin.isAdmin && (
                <div className="space-y-3 mt-3">
                  <p className="text-xs text-gray-500">You'll receive an SMS 7 days before each family member's birthday and your anniversary.</p>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Your phone number</label>
                    <input
                      type="tel"
                      value={localAdmin.reminderPhone}
                      onChange={e => setLocalAdmin(p => ({ ...p, reminderPhone: e.target.value }))}
                      placeholder="+1 (555) 000-0000"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Wedding anniversary date</label>
                    <input
                      type="date"
                      value={localAdmin.anniversaryDate}
                      onChange={e => setLocalAdmin(p => ({ ...p, anniversaryDate: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <button
                    onClick={handleSaveAdmin}
                    className={`w-full py-2 rounded-lg font-medium touch-manipulation text-sm ${
                      adminSaved ? "bg-green-600 text-white" : "bg-purple-600 text-white hover:bg-purple-700"
                    }`}
                  >
                    {adminSaved ? "✓ Saved!" : "Save Reminders"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "vacations" && (
          <TripsTab members={members} vacationBlocks={vacationBlocks || []} onReload={onReloadVacations} />
        )}

        {activeTab === "contacts" && (
          <ExternalContactsTab />
        )}

        {activeTab === "venues" && (
          <VenuesTab />
        )}

        <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium touch-manipulation"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
